<template>
  <div
    ref="tableContainer"
  >
    <UITable
      :columns="fieldsOutboundNonKomship"
      :items="fetcherOutbounds.data"
      :loading="fetcherOutbounds.isLoading"
      :selectable="hasConfirmedStatus"
      :selected.sync="selected"
      item-key="id"
      max-height="65vh"
      class="font-medium"
    >
      <template #cell(id)="{ value, item }">
        <div>
          <p
            class="mb-[4px]"
            :title="value"
          >
            {{ value }}
          </p>
          <p
            v-b-tooltip.hover.top="getLabelLastDownloadTooltip(item)"
            class="mb-0 font-normal text-[12px]"
            :class="item.printed_at !== null ? '!text-[#3B82F6]' : '!text-[#F95E16]'"
          >
            {{ item.printed_at !== null ? 'Label telah diunduh' : 'Label belum diunduh' }}
          </p>
        </div>
      </template>
      <template #cell(submit_date)="{ item }">
        <div class="text-capitalize text-left">
          <p class="mb-[4px]">
            {{ formatSubmitDate(item.submit_date).date }}
          </p>
          <p class="mb-0 font-normal">
            {{ formatSubmitDate(item.submit_date).hour }}
          </p>
        </div>
      </template>
      <template #cell(total_orders)="{ item, value }">
        <span>{{ item.total_orders_processed || 0 }}/{{ value || 0 }}</span>
      </template>
      <template #cell(status)="{ item }">
        <StatusPackingPill :status="item.status" />
      </template>
      <template #cell(rincian)="{ item }">
        <router-link
          :to="`/outbound/non-komship/${item.id}`"
          class="hover:underline !text-[#F95E16]"
        >
          Lihat Detail
        </router-link>
      </template>

      <template #footer>
        <LoadMoreData :fetcher="fetcherOutbounds" />
      </template>
    </UITable>

    <div
      v-if="hasSelectedOutbound && hasConfirmedStatus && hasOutboundData"
      class="flex items-center justify-between sticky bottom-0 bg-white p-[16px] gap-[8px] z-[10]"
    >
      <p class="!mb-0">
        {{ selected.length }} order terpilih
      </p>
      <b-button
        variant="primary"
        :disabled="disableBulkUpdateButton"
        @click="showModalBulkUpdate"
      >
        Order Terkirim
      </b-button>
    </div>

    <ModalSelectAll
      ref="modalSelectAll"
      @confirm="handleBulkUpdate"
    />

  </div>

</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { toastError } from '@/libs/toastification'
import {
  DAY_MONTH_YEAR, TIME, YEAR_MONTH_DAY, DAY_FULL_DATE_TIME, TIME_DOT,
} from '@/libs/filterDate'
import { Table as UITable } from '@/@core/components/ui'
import axios from 'axios'
import { fieldsOutboundNonKomship } from './config'
import StatusPackingPill from './StatusPackingPill.vue'
import LoadMoreData from './LoadMoreData.vue'
import ModalSelectAll from './ModalSelectAll.vue'

export default {
  name: 'Table',
  components: {
    UITable,
    StatusPackingPill,
    LoadMoreData,
    ModalSelectAll,
  },
  data() {
    return {
      fetcherOutbounds: null,
      fieldsOutboundNonKomship,
      selected: [],
      errorBulkUpdate: [],
    }
  },
  computed: {
    allOutboundIds() {
      return this.fetcherOutbounds?.data?.map(item => item.id)
    },
    allOutboundSelected() {
      return this.selected.length === this.allOutboundIds.length
    },
    hasOutboundData() {
      return this.fetcherOutbounds?.data?.length > 0
    },
    hasSelectedOutbound() {
      return this.selected?.length > 0
    },
    hasConfirmedStatus() {
      return this.$route.query.status === 'confirmed'
    },
    disableBulkUpdateButton() {
      return !this.hasSelectedOutbound || this.fetcherOutbounds?.isLoading || this.fetcherOutbounds?.isFetching
    },
  },
  watch: {
    '$route.query.status': {
      handler() {
        this.selected = []
      },
    },
  },
  created() {
    this.initializeFetcherOutbounds()
  },
  mounted() {
    // Initial fetch is handled by parent component (NonKomship.vue) via applyFilters()
    // Setup IntersectionObserver untuk infinite scroll yang lebih efisien
    this.observer = new IntersectionObserver(this.handleIntersect, {
      root: null,
      rootMargin: '200px 0px 0px 0px',
      threshold: 0.1,
    })

    this.$nextTick(() => {
      if (this.$refs.infiniteLoader) {
        this.observer.observe(this.$refs.infiniteLoader)
      }
    })
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.fetcherOutbounds && this.fetcherOutbounds.cancel) {
      this.fetcherOutbounds.cancel()
    }
  },
  methods: {
    /**
     * Initialize paginated fetcher instance for non-komship outbounds.
     */
    initializeFetcherOutbounds() {
      const fnFetch = ({
        offset, limit, partnerName, partnerId, outboundId, status, signal, startDate, endDate,
      }) => {
        const params = {
          offset,
          limit,
        }
        if (partnerName) params.keyword = partnerName
        if (partnerId) params.partner_id = partnerId
        if (outboundId) params.outbound_id = outboundId
        if (status) params.status = status
        if (startDate) params.start_date = startDate
        if (endDate) params.end_date = endDate

        return this.$http_new.get('kompack/api/v1/mitra/outbounds', {
          params,
          signal,
        })
          .then(response => {
            const { data } = response.data
            return { data }
          })
          .catch(err => {
            if (axios.isCancel(err) || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
              throw err
            }
            toastError('Gagal mengambil data request penyimpanan, silahkan coba lagi')
            return { data: [] }
          })
      }
      this.fetcherOutbounds = createPaginatedFetcher(fnFetch, 0, 50)
    },
    handleIntersect(entries) {
      const [entry] = entries
      if (!entry || !entry.isIntersecting) return

      if (this.fetcherOutbounds && this.fetcherOutbounds.hasMore && !this.fetcherOutbounds.isLoading) {
        this.fetcherOutbounds.fetchData()
      }
    },
    /**
     * Format submit date into separated date and hour strings.
     * @param {string} value
     * @returns {{ date: string, hour: string }}
     */
    formatSubmitDate(value) {
      if (!value) {
        return {
          date: '-',
          hour: '-',
        }
      }

      return {
        date: DAY_MONTH_YEAR(value),
        hour: TIME(value),
      }
    },
    /**
     * Apply date range, partner, and status filters to the outbound fetcher.
     * @param {Object} filters
     */
    applyFilters({
      date_range: dateRange,
      outbound_id: outboundId,
      partner_id: partnerId,
      partner_name: partnerName,
      status,
    }) {
      if (this.fetcherOutbounds && this.fetcherOutbounds.cancel) {
        this.fetcherOutbounds.cancel()
      }
      const { startDate, endDate } = dateRange || {}
      this.fetcherOutbounds.fetchData({
        reset: true,
        options: {
          startDate: startDate ? YEAR_MONTH_DAY(startDate) : undefined,
          endDate: endDate ? YEAR_MONTH_DAY(endDate) : undefined,
          outboundId,
          partnerId,
          partnerName: partnerName || undefined,
          status,
        },
      })
    },
    /**
     * Toggle select all checkbox. If there is more data, fetch it first
     */
    async toggleSelectAll(value) {
      if (value && this.fetcherOutbounds.hasMore) {
        await this.fetcherOutbounds.fetchData()
        await this.toggleSelectAll(true)
        return
      }
      this.selected = value ? this.allOutboundIds : []
    },
    /**
     * Show the progress of bulk update process
     */
    handleBulkUpdate() {
      this.$refs.modalSelectAll.show({
        title: 'Sedang memproses order',
        description: 'Beberapa order sedang diproses menjadi Terkirim. Mohon ditunggu sebentar ya',
        status: 'WARNING',
        progressMax: this.selected.length,
        progressText: `0/${this.selected.length} order diproses`,
        processedCount: 0,
        processFn: this.processBulkUpdate,
      })
    },
    /**
     * Process bulk update sequentially
     */
    async processBulkUpdate() {
      let processedCount = 0

      for (let index = 0; index < this.selected.length; index += 1) {
        const outboundId = this.selected[index]
        // eslint-disable-next-line no-await-in-loop
        await this.mutateBulkUpdate({
          outboundId,
        })

        processedCount += 1
        this.$refs.modalSelectAll.updateProgress({
          text: `${processedCount}/${this.selected.length} order diproses`,
          count: processedCount,
        })
      }

      this.$refs.modalSelectAll.show({
        title: 'Order Terkirim',
        description: 'Order yang kamu konfirmasi, siap untuk dipickup oleh pihak ekspedisi.',
        status: 'SUCCESS',
        confirmLabel: 'Selesai',
        onConfirm: () => {
          this.$refs.modalSelectAll.hide()
          this.selected = []
          this.isAllSelected = false
          this.isIndeterminate = false
          this.$router.push({
            name: 'outbound-non-komship',
            query: {
              status: 'shipped',
            },
          })
        },
      })
    },
    /**
     * Mutate bulk update. Consist of:
     * 1. Get detail order
     * 2. Update order status to shipped
     */
    async mutateBulkUpdate({ outboundId }) {
      try {
        const { data } = await this.$http_new.get(`/kompack/api/v1/mitra/outbounds/${outboundId}/detail`)
        const payload = {
          outbound_detail_ids: data.data.order_summary.map(item => item.outbound_detail_id),
        }

        await this.$http_new.put(`/kompack/api/v1/mitra/outbounds/${outboundId}/ship`, payload)
      } catch (error) {
        this.errorBulkUpdate.push(outboundId)
      }
    },
    // show modal confirmation before bulk update process
    showModalBulkUpdate() {
      this.$refs.modalSelectAll.show({
        title: 'Konfirmasi Order',
        description: 'Apakah kamu yakin memproses beberapa order Dipacking menjadi Terkirim?',
        status: 'WARNING',
        confirmLabel: 'Ya',
        onConfirm: this.handleBulkUpdate,
        cancelLabel: 'Batal',
        onCancel: () => {
          this.$refs.modalSelectAll.hide()
        },
      })
    },
    /**
     * Generate tooltip string for label download timestamp.
     * @param {Object} item
     * @returns {string}
     */
    getLabelLastDownloadTooltip(item) {
      const timestamp = item.printed_at
      if (!timestamp) return ''
      return `Label pertama diunduh pada ${DAY_FULL_DATE_TIME(timestamp)}`
    },
  },
}
</script>
